import useSWR from 'swr';
import { useState } from 'react';
import api from './axios';
import { authService } from '../auth/authService';
import { LoginCredentials, LoginResponse } from '../auth/types';
import { RegisterFormData } from '../validations/auth';

const AUTH_ENDPOINTS = {
  LOGIN: '/api/shop/login',
  LOGOUT: '/api/shop/logout',
  REFRESH: '/api/shop/refresh-token',
  REGISTER: '/api/shop/register',
  REGISTRATION_OPTIONS: '/api/shop/registration-options',
  CHECK_EMAIL: '/api/shop/check-email',
  MEMBERSHIP_SUMMARY: '/api/shop/membership/summary',
};

// SWR fetcher function
const fetcher = (url: string) => api.get(url).then(res => res.data);

// Login function
export const loginUser = async (credentials: LoginCredentials): Promise<LoginResponse> => {
  const response = await api.post<LoginResponse>(AUTH_ENDPOINTS.LOGIN, credentials);
  return response.data;
};

// Register function
export const registerUser = async (userData: RegisterFormData): Promise<LoginResponse> => {
  return await authService.register(userData);
};

// Logout function
export const logoutUser = async (): Promise<void> => {
  try {
    await api.post(AUTH_ENDPOINTS.LOGOUT);
  } catch (error) {
    console.error('Logout error:', error);
  }
};

// Refresh token function
export const refreshToken = async (refreshToken: string): Promise<LoginResponse> => {
  const response = await api.post<LoginResponse>(AUTH_ENDPOINTS.REFRESH, {
    refresh_token: refreshToken,
  });
  return response.data;
};

// Fetch registration options function
export async function fetchRegistrationOptions() {
  const response = await api.get(AUTH_ENDPOINTS.REGISTRATION_OPTIONS);
  return response.data;
}

// Check email existence function
export const checkEmailExists = async (email: string): Promise<{exists: boolean, message?: string}> => {
  const response = await api.post(AUTH_ENDPOINTS.CHECK_EMAIL, { email });
  
  // Convert API response format to expected format
  const apiData = response.data;
  const convertedResponse = {
    exists: !apiData.available, // available: false means email exists
    message: apiData.message
  };
  
  return convertedResponse;
};

// Membership summary types
export interface MembershipLevel {
  id: number;
  name: string;
  nameZh?: string;
  nameCn?: string;
  description: string;
  descriptionZh?: string;
  descriptionCn?: string;
  minSpendAmount: number;
  maxSpendAmount: number;
  discountPercentage: number;
  color: string;
  icon: string;
  benefits: string[];
  cardImageUrl: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CurrentMembership {
  id: number;
  customerId: string;
  membershipLevelId: number;
  membershipNumber: string;
  totalSpent: number;
  expirationDate?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  membershipLevel: MembershipLevel;
}

export interface MembershipSummary {
  success: boolean;
  customer: {
    id: string;
    name: string;
    email: string;
    totalSpent: number;
  };
  currentMembership: CurrentMembership;
  currentTier: {
    name: string;
    discountPercentage: number;
  };
  nextTier?: {
    name: string;
    target: number;
    remainingToSpend: number;
  };
  membershipLevels: MembershipLevel[];
  calculations: {
    currentSpent: number;
    nextTierTarget: number;
    remainingToSpend: number;
    hasNextTier: boolean;
  };
}

// Get membership summary function
export const getMembershipSummary = async (): Promise<MembershipSummary> => {
  const response = await api.get(AUTH_ENDPOINTS.MEMBERSHIP_SUMMARY);
  return response.data;
};

// Custom login hook (better approach for mutations)
export const useLogin = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<LoginResponse | null>(null);

  const login = async (credentials: LoginCredentials) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await loginUser(credentials);
      setData(result);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Login failed');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    login,
    data,
    error,
    isLoading,
  };
};

// Custom register hook
export const useRegister = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<LoginResponse | null>(null);

  const register = async (userData: RegisterFormData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await registerUser(userData);
      setData(result);
      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Registration failed');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    register,
    data,
    error,
    isLoading,
  };
};

// Custom email check hook
export const useEmailCheck = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [result, setResult] = useState<{exists: boolean, message?: string} | null>(null);

  const checkEmail = async (email: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await checkEmailExists(email);
      setResult(response);
      return response;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Email check failed');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    checkEmail,
    result,
    error,
    isLoading,
  };
};

// Custom membership summary hook
export const useMembershipSummary = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<MembershipSummary | null>(null);

  const fetchSummary = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await getMembershipSummary();
      setData(response);
      return response;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch membership summary');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    fetchSummary,
    data,
    error,
    isLoading,
  };
};

// Hook to check if user is authenticated (for API calls)
export const useAuthStatus = () => {
  const { data, error, mutate } = useSWR<LoginResponse>(
    '/api/shop/me', // Assuming there's a me endpoint to check auth status
    fetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      errorRetryCount: 3,
    }
  );

  return {
    user: data?.customer || null,
    isAuthenticated: !!data?.customer,
    isLoading: !error && !data,
    error,
    mutate,
  };
};
